import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import { useFetchRoleExplainer } from "@/app/Views/FitCheck/config/api";

import { isValidRoleExplanations, JobDetails, RoleExplanations } from "../utils/helpers";
import { RoleExplainerFailureReason, RoleExplainerTrigger } from "./useJobPageAnalytics";

interface UseRoleExplainerOptions {
	/** Called when an explanation loads and is valid enough to render. */
	onLoaded?: (trigger: RoleExplainerTrigger) => void;
	/** Called when a run finishes with no usable explanation. */
	onFailed?: (reason: RoleExplainerFailureReason, trigger: RoleExplainerTrigger) => void;
}

interface UseRoleExplainerReturn {
	roleExplanations: RoleExplanations | null;
	explainerIsLoading: boolean;
	explainerHasError: boolean;
	handleRoleExplainer: (trigger?: RoleExplainerTrigger) => void;
}

/**
 * Hook to manage role explainer API calls and state
 * @param jobDetails - The current job details
 * @param options - Callbacks for the load outcome, used for analytics
 * @returns Role explainer state and handler
 */
export function useRoleExplainer(
	jobDetails: JobDetails,
	options: UseRoleExplainerOptions = {},
): UseRoleExplainerReturn {
	const [explainerIsLoading, setExplainerIsLoading] = useState(false);
	const [explainerHasError, setExplainerHasError] = useState(false);
	const [roleExplanations, setRoleExplanations] = useState<RoleExplanations | null>(null);
	const fetchRoleExplainer = useFetchRoleExplainer();

	// Held in a ref so a fresh callback identity does not rebuild handleRoleExplainer,
	// which the auto-load effect in ViewJob depends on.
	const optionsRef = useRef(options);
	optionsRef.current = options;

	const handleRoleExplainer = useCallback(
		async (trigger: RoleExplainerTrigger = "click") => {
			setExplainerIsLoading(true);
			setExplainerHasError(false);
			try {
				// Served from the local one-hour cache when the same job was requested before.
				const response = await fetchRoleExplainer({
					jobDetails: jobDetails as unknown as Record<string, unknown>,
					jobId: jobDetails._id || "",
					userId: "", // Not used by the API but required by type
				});
				// API returns results with RoleExplanations shape, but TypeScript types are mismatched
				const results = (response as unknown as { results?: unknown })?.results ?? null;
				if (isValidRoleExplanations(results)) {
					setRoleExplanations(results);
					optionsRef.current.onLoaded?.(trigger);
				} else {
					setRoleExplanations(null);
					setExplainerHasError(true);
					optionsRef.current.onFailed?.("invalid_response", trigger);
					toast.warning("Role Explainer is not available, Try again later.");
				}
			} catch {
				setExplainerHasError(true);
				optionsRef.current.onFailed?.("api_error", trigger);
				toast.error("Failed to run Role Explainer. Please try again.");
			} finally {
				setExplainerIsLoading(false);
			}
		},
		[fetchRoleExplainer, jobDetails],
	);

	return {
		roleExplanations,
		explainerIsLoading,
		explainerHasError,
		handleRoleExplainer,
	};
}
